<template>
  <q-btn-toggle
    class="tm-button-toggle"
    :class="{
      'tm-button-toggle--bordered': bordered,
      'tm-button-toggle--rounded': rounded,
      'tm-button-toggle--size-large': size === 'large',
    }"
    :model-value="modelValue"
    :options="options"
    :spread="spread"
    :disable="disabled"
    :padding="padding"
    unelevated
    @update:model-value="$emit('update:model-value', $event)"
  >
    <template
      v-for="(_, name) in $slots"
      v-slot:[name]
      :key="name"
    >
      <slot :name="name" />
    </template>
  </q-btn-toggle>
</template>

<script setup lang="ts" generic="T extends string">
import type { TmButtonToggleOption } from '@/components/shared/types'

defineProps<{
  modelValue: string
  options: TmButtonToggleOption<T>[]
  spread?: boolean
  bordered?: boolean
  rounded?: boolean
  disabled?: boolean
  size?: 'default' | 'large'
  padding?: string | undefined
}>()

defineEmits<{
  'update:model-value': [T]
}>()
</script>

<style lang="scss" scoped>
$rootClassName: 'tm-button-toggle';

.#{$rootClassName} {
  height: var(--tm-button-toggle-height, 28px);
  box-sizing: content-box;
  background: $input;
  padding: 2px;

  :deep(.q-btn) {
    text-transform: none;
    width: var(--tm-button-toggle-width, auto);
    min-height: var(--tm-button-toggle-height, 28px);
    border-radius: $border-radius;
    font-weight: $semi-bold-font;
    color: $emphasize;

    &.bg-primary {
      /* active */
      background: $white !important;
      color: $emphasize !important;
      box-shadow: 0 1px 4px rgba($black, 0.22);
    }

    &:not(:first-child) {
      margin-left: 2px;

      &:after {
        content: '';
        background: $gray3;
        width: 1px;
        position: absolute;
        left: -1px;
        top: 6px;
        bottom: 6px;
      }
    }
  }

  :deep(.on-left) {
    margin-right: 4px;
  }

  &--bordered {
    border: 1px solid $border-button;
  }

  &.#{$rootClassName}--rounded {
    border-radius: 6px;

    :deep(.q-btn) {
      border-radius: 4px;
    }
  }

  :deep(.q-icon) {
    padding-left: 4px;
    color: $neutral;
  }

  &--size-large {
    --tm-button-toggle-height: 36px;
    padding: 3px;

    &.#{$rootClassName}--rounded {
      border-radius: 8px;

      :deep(.q-btn) {
        border-radius: 6px;
      }
    }

    :deep(.q-icon) {
      font-size: 16px;
    }
  }
}
</style>
